/**
 * Paper Trading Number & Currency Formatters.
 *
 * Provides Indian Rupee currency formatting, decimal numbers, and price change representations.
 */

export interface PriceChangeFormatted {
  text: string;
  diff: number;
  pct: number;
  isPositive: boolean;
}

/**
 * Formats a number as Indian Rupee currency with explicit negative handling (e.g. ₹1,234.50 or -₹500.00).
 */
export function formatCurrencyInr(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return "₹0.00";
  const absFormatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `-₹${absFormatted}` : `₹${absFormatted}`;
}

/**
 * Formats a numeric value using Indian comma separation and fixed decimals.
 */
export function formatNumberInr(
  n: number | null | undefined,
  decimals: number = 2,
  fallback: string = "0.00",
): string {
  if (n == null || isNaN(n)) return fallback;
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Computes price difference and percentage against a reference price (+5.00 (+5.00%)).
 */
export function formatPriceChangePercent(
  value: number | null | undefined,
  ref: number | null | undefined,
): PriceChangeFormatted | null {
  if (ref == null || ref === 0 || value == null || isNaN(value) || isNaN(ref)) {
    return null;
  }

  const diff = value - ref;
  const pct = (diff / ref) * 100;
  const sign = diff >= 0 ? "+" : "";
  const text = `${sign}${diff.toFixed(2)} (${sign}${pct.toFixed(2)}%)`;

  return {
    text,
    diff,
    pct,
    isPositive: diff >= 0,
  };
}

/**
 * Formats difference and percentage for paper terminal header strips (+12.50 / +1.20%).
 */
export function formatStripChange(
  value: number | null | undefined,
  ref: number | null | undefined,
): string {
  if (value == null || ref == null || isNaN(value) || isNaN(ref) || ref === 0) {
    return "--";
  }
  const chg = value - ref;
  const pct = (chg / ref) * 100;
  const sign = chg >= 0 ? "+" : "";
  return `${sign}${chg.toFixed(2)} / ${sign}${pct.toFixed(2)}%`;
}
